import { ChangeDetectorRef, Injectable } from '@angular/core';

/**
 * The AGU app surface's repaint boundary (Research Accelerator, OEP, …) — the surface's replacement for
 * the Angular zone, which MJExplorer does not have.
 *
 * WHY THIS EXISTS. MJExplorer boots with
 * `platformBrowserDynamic().bootstrapModule(AppModule)`
 * (apps/MJExplorer/src/main.ts:5). In Angular 21 that path is ZONELESS by
 * default: `PlatformRef.bootstrapModuleFactory` prepends
 * `provideZonelessChangeDetectionInternal()` to every module app's providers
 * (node_modules/@angular/core/fesm2022/core.mjs:723), and that binds
 * `NgZone → NoopNgZone` plus `ZONELESS_ENABLED → true`
 * (node_modules/@angular/core/fesm2022/_debug_node-chunk.mjs:18459-18470).
 * `NoopNgZone.run(fn)` simply calls `fn()`
 * (node_modules/@angular/core/fesm2022/_untracked-chunk.mjs:2561-2563) — it
 * never forks the `'angular'` zone
 * (_untracked-chunk.mjs:2478-2490) — so `NgZone.isInAngularZone()` is false
 * everywhere, `Zone.current.name` stays `<root>`, and re-entering the zone at
 * this surface's boundary is not a thing that can be done: there is no zone to
 * re-enter. zone.js is still shipped in `polyfills` (apps/MJExplorer/angular.json:19)
 * and still patches the browser, but nothing connects it to change detection.
 *
 * With no zone, change detection runs only when something explicitly notifies
 * Angular's scheduler: a template event listener, `markForCheck()`, a signal
 * write, `async` pipe, or an outright `detectChanges()`/`tick()`. A plain
 * `this.loading = false` after an `await` notifies nothing — which is exactly
 * the live defect (first seen in the Research Accelerator): every page finished its load with correct state and a
 * frozen DOM until an unrelated click happened to tick change detection.
 *
 * WHY `detectChanges` ON THE SURFACE VIEW. `ApplicationRef.tick()` is a no-op
 * when nothing is dirty in a zoneless app — `tick()` only sets the global-check
 * flag `if (!this.zonelessEnabled)`, so `synchronize()`'s
 * `while (this.dirtyFlags !== 0)` loop never runs
 * (_debug_node-chunk.mjs:11904-11908, 11942-11950). Refreshing the app surface's
 * own view is both sufficient and bounded: surface components are all default
 * (CheckAlways) change detection, so one pass over the surface repaints every
 * module underneath it, and nothing outside `mj-agu-surface` is touched.
 *
 * WHY THE REFRESH IS DEFERRED TO A TASK. Callers notify from a `finally` block
 * (`ResearchApiService`), i.e. BEFORE their own caller's `await` continuation
 * has run and assigned the new state. A synchronous or microtask-scheduled
 * repaint would therefore paint the state of the *previous* frame. A task
 * (`setTimeout(0)`) is guaranteed to run after every queued promise
 * continuation, so the repaint always sees the settled state. It also
 * coalesces: a page firing four parallel loads costs one pass.
 *
 * Provided on each surface COMPONENT (not `providedIn: 'root'`, and not an
 * eagerly imported surface NgModule — that is the root injector too), alongside
 * the other per-surface services, so one surface's notify never repaints
 * another's views.
 */
@Injectable()
export class AGURenderService {
    /** Every attached surface view. With the service on the surface component that is normally the one surface; a set so Attach/Detach stay idempotent. */
    private readonly views = new Set<ChangeDetectorRef>();
    /** The pending coalesced refresh, or null when none is scheduled. */
    private scheduled: ReturnType<typeof setTimeout> | null = null;

    /** Registers a surface view. Called by `RaSurfaceComponent` on init. */
    Attach(view: ChangeDetectorRef): void {
        this.views.add(view);
    }

    /** Unregisters a surface view. Called by `RaSurfaceComponent` on destroy — a destroyed view must never be refreshed. */
    Detach(view: ChangeDetectorRef): void {
        this.views.delete(view);
        if (this.views.size === 0 && this.scheduled !== null) {
            clearTimeout(this.scheduled);
            this.scheduled = null;
        }
    }

    /**
     * Asks for a repaint once the current task and every queued promise continuation have finished. Safe to call as often as you like.
     *
     * Also marks every attached view for check right now. Angular's own zoneless tick is a
     * timer as well, and in dev mode it ends with a no-changes pass that descends into this
     * surface even when its update pass skipped it (nothing had marked it dirty): a page that
     * assigns state after a service's `finally { Notify() }` and before the deferred refresh
     * below then trips NG0100 ("Previous value: '3'. Current value: '5'", the Settings cold
     * deep link, 2026-10-24). Marking the path dirty puts this subtree on that tick's update
     * pass, so the bindings are current whichever timer fires first; the deferred refresh
     * below still covers the memo-hit case where no Angular tick is coming at all.
     */
    Notify(): void {
        for (const view of this.views) {
            try {
                view.markForCheck();
            } catch {
                // Same tolerance as Refresh(): a view gone between attach and
                // notify must not stop the others from being marked.
            }
        }
        if (this.scheduled !== null) return;
        this.scheduled = setTimeout(() => {
            this.scheduled = null;
            this.Refresh();
        }, 0);
    }

    /** Repaints every attached surface view now. Public so the surface can force a pass at its own state transitions. */
    Refresh(): void {
        for (const view of this.views) {
            try {
                view.detectChanges();
            } catch (error) {
                // One surface whose view is gone (destroyed between the notify
                // and the refresh) must not stop the others from painting.
                console.error('[agu-render] refreshing an app surface failed', error);
            }
        }
    }
}
